import { useEffect, useState } from 'react';
import {
  POLICIES_COMPANY,
  POLICIES_SUPPORT_EMAIL,
  PRIVACY_POLICY_SECTIONS,
  TERMS_SECTIONS,
  type PolicyTab,
  type PolicySection,
} from '../../../content/policiesContent';
import { AUTH_GREEN, AUTH_GREEN_HOVER } from './authTheme';

interface PoliciesTermsModalProps {
  isOpen: boolean;
  initialTab?: PolicyTab;
  onClose: () => void;
}

function PolicyBody({ sections }: { sections: PolicySection[] }) {
  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <section
          key={section.title}
          className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
        >
          <h3 className="text-sm font-semibold text-[#008540]">{section.title}</h3>
          {section.paragraphs?.map((p) => (
            <p key={p.slice(0, 40)} className="mt-2 text-sm leading-relaxed text-gray-600">
              {p}
            </p>
          ))}
          {section.bullets && section.bullets.length > 0 && (
            <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-600">
              {section.bullets.map((item) => (
                <li key={item.slice(0, 48)}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

export default function PoliciesTermsModal({
  isOpen,
  initialTab = 'privacy',
  onClose,
}: PoliciesTermsModalProps) {
  const [tab, setTab] = useState<PolicyTab>(initialTab);

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
    }
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[400] flex items-end justify-center bg-slate-900/55 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="policies-modal-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-slate-50 shadow-2xl ring-1 ring-slate-900/10 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pb-4 pt-5 text-white" style={{ background: `linear-gradient(to bottom right, ${AUTH_GREEN}, ${AUTH_GREEN_HOVER}, #064e3b)` }}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="policies-modal-title" className="text-lg font-bold tracking-tight">
                Policies &amp; Terms
              </h2>
              <p className="mt-1 text-sm text-white/80">{POLICIES_COMPANY}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <i className="ri-close-line text-xl" aria-hidden />
            </button>
          </div>

          <div className="mt-4 flex rounded-xl bg-black/20 p-1 backdrop-blur-sm">
            <button
              type="button"
              onClick={() => setTab('privacy')}
              className={`flex-1 rounded-md py-2 text-sm font-semibold transition ${
                tab === 'privacy'
                  ? 'bg-white text-[#008540] shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={() => setTab('terms')}
              className={`flex-1 rounded-md py-2 text-sm font-semibold transition ${
                tab === 'terms'
                  ? 'bg-white text-[#008540] shadow-sm'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {tab === 'privacy' ? (
            <PolicyBody sections={PRIVACY_POLICY_SECTIONS} />
          ) : (
            <PolicyBody sections={TERMS_SECTIONS} />
          )}
        </div>

        <div className="border-t border-gray-200 bg-white px-4 py-3 text-center text-xs text-gray-500">
          Questions?{' '}
          <a href={`mailto:${POLICIES_SUPPORT_EMAIL}`} className="font-medium text-[#008540] hover:underline">
            {POLICIES_SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    </div>
  );
}
